// IncidenciasTab.js — pestaña "Incidencias" de Órdenes de trabajo.
//
// Autónoma (igual que WeeklyAgenda/OrdenesAnalitica): trae sus propios
// datos, no depende de `ordenes`/`loading` de OrdenesView. Lee directo de
// Firestore `incidencias` (lectura permitida por las reglas para cualquier
// sesión válida — igual que `ordenes`), con el mismo filtro de fecha
// (Hoy/Esta semana/Todas las fechas, "Hoy" por defecto) y el mismo
// paginador con números que "Órdenes" (TodasTab/Paginacion, OrdenesView.js).
//
// Estados y tipos reales de incidencia en StelOrder (consultados vía
// /app/incidentStates y los TIPOS_INCIDENT ya usados en createIncident.js —
// no se inventan otros):
//   651452 Pendiente de gestionar · 651442 Gestionada
//   651426 Rechazada · 651430 Cerrada
//   897 SAT · 895 Instalación · 898 Presupuesto · 100 Mantenimiento
//
// Editar (descripción/prioridad/estado) va a updateIncidencia.js, que deja
// el cambio en Firebase al momento y stelSyncEstado='pendiente' para que
// syncStelOrderPendientes.js lo suba a StelOrder — mismo patrón que
// updateWorkorderState.js, nunca una llamada directa a StelOrder desde el
// navegador. Crear una incidencia nueva sigue haciéndose desde la ficha del
// cliente (ClientesView.js → IncidenceFormView.js, que ya exige verificar
// el cliente) — no se duplica ese flujo aquí.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, where, orderBy, limit, startAfter, getDocs, documentId } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';
import { Paginacion } from './OrdenesView';

const PAGE_SIZE = 25; // por página en pantalla, estilo StelOrder — igual que TodasTab
const PAGE_FETCH_SIN_RANGO = 100; // documentos por lectura a Firestore en "Todas las fechas"

export const ESTADO_INCIDENCIA_CFG = {
  pendiente_gestionar: { label: 'Pendiente de gestionar', cls: 'bg-yellow-100 text-yellow-800' },
  gestionada:          { label: 'Gestionada',             cls: 'bg-blue-100 text-blue-800' },
  rechazada:           { label: 'Rechazada',              cls: 'bg-red-100 text-red-800' },
  cerrada:             { label: 'Cerrada',                cls: 'bg-emerald-100 text-emerald-800' },
};
const ESTADO_ID_TO_KEY = { 651452: 'pendiente_gestionar', 651442: 'gestionada', 651426: 'rechazada', 651430: 'cerrada' };

const TIPO_INCIDENCIA_CFG = {
  sat:           'SAT',
  instalacion:   'Instalación',
  presupuesto:   'Presupuesto',
  mantenimiento: 'Mantenimiento',
};
const TIPO_ID_TO_KEY = { 897: 'sat', 895: 'instalacion', 898: 'presupuesto', 100: 'mantenimiento' };

const PRIORIDAD_CFG = { LOW: 'Baja', NORMAL: 'Normal', HIGH: 'Alta', VERYHIGH: 'Muy alta' };

const FMT_FECHA_ES = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' });
function hoyMadrid() { return FMT_FECHA_ES.format(new Date()); }
function sumarDias(fechaStr, dias) {
  const d = new Date(fechaStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().split('T')[0];
}
// Mismo criterio que rangoFechas() en OrdenesView.js — null = sin acotar.
function rangoFechaIncidencia(filtro) {
  const hoy = hoyMadrid();
  if (filtro === 'hoy') return { desde: hoy, hasta: sumarDias(hoy, 1) };
  if (filtro === 'semana') {
    const diaSemana = new Date(hoy + 'T12:00:00Z').getUTCDay();
    const lunes = sumarDias(hoy, -((diaSemana + 6) % 7));
    return { desde: lunes, hasta: sumarDias(lunes, 7) };
  }
  return null;
}

function normalizeIncidencia(d) {
  const data = d.data();
  const estadoId = data['incident-state-id'];
  const tipoId = data['incident-type-id'];
  return {
    id: d.id,
    referencia: data['full-reference'] || data.reference || `#${d.id}`,
    clienteNombre: data['customer-name'] || '',
    accountId: data['account-id'] ?? null,
    descripcion: data.description || data.comments || '',
    // Fecha de entrada real (creation-date) — no `date`, que en una
    // incidencia no es "cuándo entró" (mismo criterio ya aplicado en el
    // Centro de Control de Operaciones).
    fecha: data['creation-date'] ? String(data['creation-date']).slice(0, 10) : null,
    estado: ESTADO_ID_TO_KEY[estadoId] || null,
    tipo: TIPO_ID_TO_KEY[tipoId] || null,
    prioridad: data.priority || null,
    addressId: data['address-id'] ?? null,
    pdfPath: data['pdf-path'] || null,
    telefono: data.phone || null,
    deleted: !!data.deleted,
  };
}

// La mayoría de incidencias llegan del sync de StelOrder (raw, solo
// account-id) — igual que las órdenes, sin nombre de cliente embebido.
// "customer-name" solo lo trae el espejo inmediato de createIncident.js
// (incidencia creada desde la app). Sin este join, la lista sale sin
// nombres para casi todo — mismo patrón que enriquecerConClientes() en
// OrdenesView.js, copiado aquí (colección `clientes`, hasta 30 ids por
// consulta 'in').
async function enriquecerConClientes(incidencias) {
  const faltantes = [...new Set(
    incidencias.filter(i => !i.clienteNombre && i.accountId).map(i => String(i.accountId))
  )];
  if (faltantes.length === 0) return incidencias;
  const clientesPorId = new Map();
  const chunks = [];
  for (let i = 0; i < faltantes.length; i += 30) chunks.push(faltantes.slice(i, i + 30));
  await Promise.all(chunks.map(async chunk => {
    try {
      const snap = await getDocs(query(collection(db, 'clientes'), where(documentId(), 'in', chunk)));
      snap.docs.forEach(d => clientesPorId.set(d.id, d.data()));
    } catch { /* best-effort — si falla, la incidencia se queda sin nombre, no rompe la lista */ }
  }));
  return incidencias.map(inc => {
    if (inc.clienteNombre || !inc.accountId) return inc;
    const c = clientesPorId.get(String(inc.accountId));
    if (!c) return inc;
    return { ...inc, clienteNombre: c.name || c['legal-name'] || '' };
  });
}

function EstadoBadge({ estado }) {
  const cfg = ESTADO_INCIDENCIA_CFG[estado] || { label: estado || 'Sin estado', cls: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
}

const fCls = 'border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';

export default function IncidenciasTab({ onNavigate }) {
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filtroFecha, setFiltroFecha] = useState('hoy');
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [page, setPage] = useState(1);
  const [seleccionada, setSeleccionada] = useState(null);

  const cargar = useCallback(async (cursor = null) => {
    if (!cursor) setLoading(true); else setLoadingMore(true);
    setError('');
    try {
      const rango = rangoFechaIncidencia(filtroFecha);
      let snap;
      if (rango) {
        const q = query(
          collection(db, 'incidencias'),
          where('creation-date', '>=', rango.desde), where('creation-date', '<', rango.hasta),
          orderBy('creation-date', 'desc'), limit(1000),
        );
        snap = await getDocs(q);
      } else {
        const base = [collection(db, 'incidencias'), orderBy('creation-date', 'desc'), limit(PAGE_FETCH_SIN_RANGO)];
        const q = cursor ? query(...base, startAfter(cursor)) : query(...base);
        snap = await getDocs(q);
      }
      const nuevas = await enriquecerConClientes(snap.docs.map(normalizeIncidencia));
      setIncidencias(prev => (cursor && !rango ? [...prev, ...nuevas] : nuevas));
      if (rango) {
        setLastDoc(null); setHasMore(false);
      } else {
        setLastDoc(snap.docs[snap.docs.length - 1] || cursor);
        setHasMore(snap.docs.length === PAGE_FETCH_SIN_RANGO);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filtroFecha]);

  // Recarga desde cero cada vez que cambia el filtro de fecha — no se
  // acumula sobre lo anterior (mismo criterio que OrdenesView.js).
  useEffect(() => {
    setIncidencias([]); setLastDoc(null); setHasMore(false);
    cargar();
  }, [filtroFecha]); // eslint-disable-line

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return incidencias.filter(inc => {
      if (inc.deleted) return false; // borrado lógico — nunca se muestran
      if (filtroEstado && inc.estado !== filtroEstado) return false;
      if (!q) return true;
      return inc.referencia.toLowerCase().includes(q) || inc.clienteNombre.toLowerCase().includes(q) || inc.descripcion.toLowerCase().includes(q);
    });
  }, [incidencias, busqueda, filtroEstado]);

  useEffect(() => { setPage(1); }, [filtradas]);
  const totalPages = Math.max(1, Math.ceil(filtradas.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const visibles = filtradas.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE);

  const actualizarLocal = (id, cambios) => {
    setIncidencias(prev => prev.map(i => (i.id === id ? { ...i, ...cambios } : i)));
    setSeleccionada(prev => (prev && prev.id === id ? { ...prev, ...cambios } : prev));
  };

  if (error) {
    return (
      <div className="space-y-3">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>
        <button onClick={() => cargar()} className="text-xs font-bold text-red-600 hover:text-red-800">Reintentar</button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-wrap items-center gap-2 flex-shrink-0 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <input
            type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por referencia, cliente o descripción..."
            className={`w-full ${fCls}`}
          />
        </div>
        <select value={filtroFecha} onChange={e => setFiltroFecha(e.target.value)} className={fCls}>
          <option value="hoy">Hoy</option>
          <option value="semana">Esta semana</option>
          <option value="">Todas las fechas</option>
        </select>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} className={fCls}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_INCIDENCIA_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => cargar()} className="text-xs font-bold text-gray-500 hover:text-gray-700 px-2">↻ Actualizar</button>
        <span className="text-xs text-gray-400 whitespace-nowrap">{filtradas.length} incidencias</span>
      </div>

      {/* "Cargar más" solo en "Todas las fechas" (sin acotar) — mismo
          criterio que la pestaña "Órdenes": con un rango de fecha activo
          (Hoy/Semana) ya se trae todo lo que hay. */}
      {!filtroFecha && (hasMore || loadingMore) && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-500 mb-2 flex-shrink-0">
          <span>{incidencias.length} incidencias cargadas{hasMore ? ' — puede haber más' : ''}</span>
          {hasMore && (
            <button onClick={() => cargar(lastDoc)} disabled={loadingMore}
              className="px-3 py-1 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {loadingMore ? 'Cargando…' : 'Cargar más'}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>
      ) : filtradas.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">Sin incidencias con estos filtros.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs uppercase border-b sticky top-0 bg-white">
                  <th className="p-3">Ref.</th>
                  <th className="p-3">Cliente</th>
                  <th className="p-3 hidden md:table-cell">Descripción</th>
                  <th className="p-3 hidden lg:table-cell">Fecha</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3 hidden sm:table-cell">Tipo</th>
                  <th className="p-3 hidden lg:table-cell">Prioridad</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(inc => (
                  <tr key={inc.id} onClick={() => setSeleccionada(inc)} className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer">
                    <td className="p-3 font-mono text-xs text-gray-700 whitespace-nowrap">{inc.referencia}</td>
                    <td className="p-3 text-gray-700 truncate max-w-[160px]">{inc.clienteNombre || '—'}</td>
                    <td className="p-3 hidden md:table-cell text-gray-500 truncate max-w-[280px]">{inc.descripcion || '—'}</td>
                    <td className="p-3 hidden lg:table-cell text-gray-500 whitespace-nowrap">{inc.fecha || '—'}</td>
                    <td className="p-3"><EstadoBadge estado={inc.estado} /></td>
                    <td className="p-3 hidden sm:table-cell text-gray-500">{TIPO_INCIDENCIA_CFG[inc.tipo] || '—'}</td>
                    <td className="p-3 hidden lg:table-cell text-gray-500">{PRIORIDAD_CFG[inc.prioridad] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 border-t border-gray-50 flex-shrink-0">
            <span className="text-[11px] text-gray-400">
              {(pageClamped - 1) * PAGE_SIZE + 1}–{Math.min(pageClamped * PAGE_SIZE, filtradas.length)} de {filtradas.length}
            </span>
            <Paginacion page={pageClamped} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      )}

      {seleccionada && (
        <IncidenciaDetallePanel
          incidencia={seleccionada}
          onClose={() => setSeleccionada(null)}
          onUpdate={actualizarLocal}
          onDeleted={(id) => { setIncidencias(prev => prev.filter(i => i.id !== id)); setSeleccionada(null); }}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

function IncidenciaDetallePanel({ incidencia, onClose, onUpdate, onDeleted, onNavigate }) {
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [editing, setEditing] = useState(false);
  const [descripcion, setDescripcion] = useState(incidencia.descripcion);
  const [prioridad, setPrioridad] = useState(incidencia.prioridad || 'NORMAL');
  const [estado, setEstado] = useState(incidencia.estado || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setDescripcion(incidencia.descripcion);
    setPrioridad(incidencia.prioridad || 'NORMAL');
    setEstado(incidencia.estado || '');
    setEditing(false);
    setMsg('');
  }, [incidencia.id]); // eslint-disable-line

  const guardar = async () => {
    setSaving(true); setMsg('');
    try {
      const cambios = {};
      if (descripcion !== incidencia.descripcion) cambios.descripcion = descripcion;
      if (prioridad !== incidencia.prioridad) cambios.prioridad = prioridad;
      if (estado !== incidencia.estado) cambios.estado = estado;
      if (Object.keys(cambios).length === 0) { setEditing(false); return; }

      const r = await fetch('/.netlify/functions/updateIncidencia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id: incidencia.id, ...cambios }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al guardar');

      onUpdate(incidencia.id, {
        descripcion, prioridad, estado,
      });
      setEditing(false);
      setMsg('Guardado — se sincronizará con StelOrder en unos minutos.');
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Borrado lógico (deleted:true) — nunca se borra el documento de
  // Firestore ni se llama a StelOrder aquí, mismo patrón que guardar():
  // se marca y el cron lo sube. Confirmación de un clic dentro del propio
  // panel, sin usar window.confirm.
  const borrar = async () => {
    setBorrando(true); setMsg('');
    try {
      const r = await fetch('/.netlify/functions/updateIncidencia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id: incidencia.id, borrar: true }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al borrar');
      onDeleted(incidencia.id);
    } catch (e) {
      setMsg(`Error: ${e.message}`);
      setBorrando(false);
      setConfirmandoBorrado(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900 font-mono">{incidencia.referencia}</h3>
            <p className="text-xs text-gray-400">{incidencia.clienteNombre}</p>
          </div>
          <div className="flex items-center gap-3">
            {onNavigate && incidencia.tipo === 'presupuesto' && incidencia.estado !== 'cerrada' && (
              <button
                onClick={() => {
                  try {
                    sessionStorage.setItem('zapata_presupuesto_prefill', JSON.stringify({
                      clienteNombre: incidencia.clienteNombre || '',
                      clienteTelefono: incidencia.telefono || '',
                      titulo: incidencia.referencia || '',
                      descripcion: incidencia.descripcion || '',
                      parentIncidentId: incidencia.id,
                    }));
                  } catch { /* si falla el prefill, se abre el formulario en blanco igualmente */ }
                  onNavigate('v2-presupuestos');
                }}
                className="text-xs font-bold text-violet-600 hover:text-violet-800"
                title="Abre Presupuestos con los datos de este cliente precargados; al guardar, cierra esta incidencia"
              >
                💶 Crear presupuesto
              </button>
            )}
            {confirmandoBorrado ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">¿Borrar?</span>
                <button onClick={borrar} disabled={borrando} className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg px-2 py-1 disabled:opacity-50">
                  {borrando ? '…' : 'Sí, borrar'}
                </button>
                <button onClick={() => setConfirmandoBorrado(false)} disabled={borrando} className="text-xs font-bold text-gray-500 hover:text-gray-700 px-1">Cancelar</button>
              </div>
            ) : (
              <button onClick={() => setConfirmandoBorrado(true)} className="text-xs font-bold text-red-600 hover:text-red-800">Borrar</button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg">✕</button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-xs text-gray-400 mb-0.5">Fecha</p><p className="font-medium text-gray-800">{incidencia.fecha || '—'}</p></div>
            <div><p className="text-xs text-gray-400 mb-0.5">Tipo</p><p className="font-medium text-gray-800">{TIPO_INCIDENCIA_CFG[incidencia.tipo] || '—'}</p></div>
            <div><p className="text-xs text-gray-400 mb-0.5">Teléfono</p><p className="font-medium text-gray-800">{incidencia.telefono || '—'}</p></div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">PDF</p>
              {incidencia.pdfPath ? <a href={`https://${incidencia.pdfPath}`} target="_blank" rel="noreferrer" className="text-red-600 font-bold text-xs hover:underline">Ver documento →</a> : <p className="text-gray-400">—</p>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-gray-400">Estado</p>
              {!editing && <button onClick={() => setEditing(true)} className="text-xs font-bold text-red-600 hover:text-red-800">Editar →</button>}
            </div>
            {editing ? (
              <select value={estado} onChange={e => setEstado(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white">
                {Object.entries(ESTADO_INCIDENCIA_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            ) : <EstadoBadge estado={incidencia.estado} />}
          </div>

          <div>
            <p className="text-xs text-gray-400 mb-1">Prioridad</p>
            {editing ? (
              <select value={prioridad} onChange={e => setPrioridad(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white">
                {Object.entries(PRIORIDAD_CFG).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            ) : <p className="text-sm font-medium text-gray-800">{PRIORIDAD_CFG[incidencia.prioridad] || '—'}</p>}
          </div>

          <div>
            <p className="text-xs text-gray-400 mb-1">Descripción</p>
            {editing ? (
              <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={8}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:border-red-400" />
            ) : (
              <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-xl p-3 max-h-64 overflow-y-auto">{incidencia.descripcion || 'Sin descripción.'}</pre>
            )}
          </div>

          {msg && <p className={`text-xs ${msg.startsWith('Error') ? 'text-red-600' : 'text-emerald-600'}`}>{msg}</p>}
        </div>

        {editing && (
          <div className="flex items-center justify-end gap-2 p-4 border-t">
            <button onClick={() => setEditing(false)} disabled={saving} className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl disabled:opacity-50">
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
